import { TextField } from "@mui/material"
import type { TextFieldProps } from "@mui/material"
import type { DesktopDatePickerProps } from "@mui/x-date-pickers/DesktopDatePicker"
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker"
import { useSplitFieldProps } from "@mui/x-date-pickers/hooks"
import type { PickerChangeHandlerContext } from "@mui/x-date-pickers/models"
import { CalendarTodayOutlined } from "@mui-symbols-material/w300"
import dayjs from "dayjs"
import type { Dayjs } from "dayjs"
import type { ReactElement } from "react"
import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useRifm } from "rifm"

import { DATE_FORMAT, EMPTY_DATE_PLACEHOLDER } from "@/constants/formats"
import { formatMaskedDate } from "@/utils/date"

const MASK_USER_INPUT_SYMBOL = "_"
const ACCEPT_REGEX = /\d/g

const staticDateWith2DigitTokens = dayjs("2019-11-21T11:30:00.000")
const staticDateWith1DigitTokens = dayjs("2019-01-01T09:00:00.000")

function getInputValueFromValue(value: Dayjs | null, format: string): string {
  if (value === null) {
    return ""
  }

  return value.isValid() ? value.format(format) : ""
}

interface MaskedTextFieldProps extends Omit<TextFieldProps, "value" | "onChange"> {
  value: Dayjs | null
  format: string
  onChange: (value: Dayjs | null, context: PickerChangeHandlerContext<unknown>) => void
  open?: boolean
  [key: string]: any
}

export const MaskedTextField = forwardRef<HTMLDivElement, MaskedTextFieldProps>((props, ref) => {
  const { internalProps, forwardedProps } = useSplitFieldProps(props, "date")

  const { format, open } = props
  const { value, onChange } = internalProps

  const [inputValue, setInputValue] = useState<string>(() => getInputValueFromValue(value, format))
  // Tracks whether the current value→null transition was caused by the user typing an incomplete
  // date (internal) vs an external reset like Clear All or chip removal. Both produce value===null,
  // so we can't distinguish them from value alone. The flag is set before onChange bubbles back
  // from the parent and read in the effect after the re-render, so the timing is reliable:
  // internal change → skip clearing (user is still typing); external change → clear the input.
  const isInternalChange = useRef(false)

  useEffect(() => {
    if (isInternalChange.current) {
      isInternalChange.current = false
      return
    }
    setInputValue(getInputValueFromValue(value, format))
  }, [format, value])

  const handleInputValueChange = useCallback(
    (newInputValue: string) => {
      setInputValue(newInputValue)
      isInternalChange.current = true

      const newValue = dayjs(newInputValue, format, true)
      onChange(newValue, {
        validationError: newValue.isValid() ? null : "invalidDate",
      })
    },
    [format, onChange]
  )

  const rifmFormat = useMemo(() => {
    const formattedDateWith1Digit = staticDateWith1DigitTokens.format(format)
    const inferredFormatPatternWith1Digits = formattedDateWith1Digit.replace(ACCEPT_REGEX, MASK_USER_INPUT_SYMBOL)
    const inferredFormatPatternWith2Digits = staticDateWith2DigitTokens.format(format).replace(ACCEPT_REGEX, "_")

    if (inferredFormatPatternWith1Digits !== inferredFormatPatternWith2Digits) {
      throw new Error(`Mask does not support numbers with variable length such as 'M'.`)
    }

    const maskToUse = inferredFormatPatternWith1Digits

    return (valueToFormat: string) => formatMaskedDate(valueToFormat, maskToUse)
  }, [format])

  const rifmProps = useRifm({
    value: inputValue,
    onChange: handleInputValueChange,
    format: rifmFormat,
  })

  return (
    <TextField
      placeholder={EMPTY_DATE_PLACEHOLDER}
      focused={open}
      disabled={internalProps.disabled}
      slotProps={{
        input: {
          readOnly: internalProps.readOnly,
        },
      }}
      {...rifmProps}
      {...forwardedProps}
      ref={ref}
    />
  )
})

export type AerosDateValue = string | null

export interface AerosDatePickerProps extends Omit<DesktopDatePickerProps<Dayjs, false>, "value" | "onChange"> {
  value: AerosDateValue
  helperText?: string
  required?: boolean
  error?: boolean
  onChange: (value: AerosDateValue) => void
  "data-testid"?: string
}

export const AerosDatePicker = forwardRef<HTMLDivElement, AerosDatePickerProps>(
  ({ value, helperText, required, error, format, onChange, "data-testid": testId, ...props }, ref): ReactElement => {
    const dateFormat = format || DATE_FORMAT.US

    const handleChange = useCallback(
      (date: Dayjs | null) => {
        onChange(date?.isValid() ? date.format(DATE_FORMAT.ISO_18601) : null)
      },
      [onChange]
    )

    return (
      <DesktopDatePicker
        {...props}
        ref={ref}
        value={value ? dayjs(value) : null}
        format={dateFormat}
        onChange={handleChange}
        slots={{
          openPickerIcon: CalendarTodayOutlined,
          field: MaskedTextField,
        }}
        slotProps={{
          field: {
            // @ts-expect-error: Passing additional props through to MaskedTextField
            required,
            error: !!error,
            helperText,
            fullWidth: true,
            "data-testid": testId,
            // DesktopDatePicker's own internally-computed field error (from its date-range/min-max
            // validation, unrelated to this form's validation) can otherwise take precedence over
            // the `error` prop above and suppress the red border — force it explicitly instead of
            // relying on that prop alone.
            sx: error
              ? {
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: "error.main" },
                  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "error.main" },
                  "& .Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "error.main" },
                }
              : undefined,
          },
        }}
      />
    )
  }
)
